import { useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import type { ColumnDef, SortingState } from '@tanstack/react-table';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { DataTable, SortableHeader } from '@/components/ui/data-table';
import {
  ListPage,
  ListPageHeader,
  ListPageTitle,
  ListPageActions,
  ListPageCard,
  ListPageFilters,
  ListPageContent,
} from '@/components/ui/list-page';
import { useApp } from '@/components/AppLayout';
import { ItemDialog } from '@/components/ItemDialog';
import { ItemImportDialog } from '@/components/ItemImportDialog';
import { itemApi, queryFn, type Item, type ItemTypeChoice } from '@/lib/api';
import { formatAmount } from '@/lib/currency';
import { MoreVertical, Package, Plus, Upload, Pencil, Trash2 } from 'lucide-react';

const PAGE_SIZE = 25;

function ItemsContent() {
  const { currentBusiness, isViewOnly } = useApp();
  const queryClient = useQueryClient();
  const [searchQuery, setSearchQuery] = useState('');
  const [dialogOpen, setDialogOpen] = useState(false);

  // Reset to first page when search changes
  const handleSearchChange = (value: string) => {
    setSearchQuery(value);
    setPageIndex(0);
  };
  const [editingItem, setEditingItem] = useState<Item | null>(null);
  const [importDialogOpen, setImportDialogOpen] = useState(false);

  // Server-side pagination state
  const [pageIndex, setPageIndex] = useState(0);
  const [sorting, setSorting] = useState<SortingState>([{ id: 'name', desc: false }]);

  const getOrderingParam = () => {
    if (sorting.length === 0) return undefined;
    const { id, desc } = sorting[0];
    return desc ? `-${id}` : id;
  };

  const { data: itemsPage, isLoading } = useQuery({
    queryKey: ['items', currentBusiness?.id, pageIndex, sorting],
    queryFn: () => queryFn(itemApi.list(currentBusiness!.id, {
      limit: PAGE_SIZE,
      offset: pageIndex * PAGE_SIZE,
      ordering: getOrderingParam(),
    })),
    enabled: !!currentBusiness,
  });

  const items = itemsPage?.results ?? [];
  const totalCount = itemsPage?.count ?? 0;

  const { data: itemTypeChoices = [] } = useQuery({
    queryKey: ['item-types'],
    queryFn: () => queryFn(itemApi.getItemTypes()),
  });

  const invalidateItems = () => queryClient.invalidateQueries({ queryKey: ['items'] });

  const handleDeleteItem = async (id: string) => {
    if (!confirm('Are you sure you want to delete this item?')) return;
    const response = await itemApi.delete(id);
    if (response.error) {
      alert(response.error);
    } else {
      invalidateItems();
    }
  };

  const openAddDialog = () => {
    setEditingItem(null);
    setDialogOpen(true);
  };

  const openEditDialog = (item: Item) => {
    setEditingItem(item);
    setDialogOpen(true);
  };

  const handleDialogClose = () => {
    setDialogOpen(false);
    setEditingItem(null);
  };

  const filteredItems = items.filter((item) => {
    const query = searchQuery.toLowerCase();
    return (
      item.name.toLowerCase().includes(query) ||
      item.code.toLowerCase().includes(query) ||
      item.description.toLowerCase().includes(query)
    );
  });

  const getItemTypeLabel = (value: string) => {
    const choice = itemTypeChoices.find(([v]) => v === value);
    return choice ? choice[1] : value;
  };

  const columns: ColumnDef<Item>[] = [
    {
      accessorKey: 'name',
      header: ({ column }) => <SortableHeader column={column} title="Name" />,
      cell: ({ row }) => (
        <div className="min-w-0 max-w-md">
          <div className="text-sm font-medium">{row.original.name}</div>
          {row.original.description && (
            <div className="text-xs text-muted-foreground line-clamp-2">
              {row.original.description}
            </div>
          )}
        </div>
      ),
    },
    {
      accessorKey: 'code',
      header: ({ column }) => <SortableHeader column={column} title="Code" />,
      cell: ({ row }) => (
        <span className="text-sm">{row.getValue('code') || '-'}</span>
      ),
    },
    {
      accessorKey: 'item_type',
      header: 'Type',
      cell: ({ row }) => (
        <span className="text-xs px-2 py-1 rounded-full bg-muted text-muted-foreground">
          {getItemTypeLabel(row.getValue('item_type'))}
        </span>
      ),
    },
    {
      accessorKey: 'rate',
      header: ({ column }) => (
        <div className="flex justify-end">
          <SortableHeader column={column} title="Rate" />
        </div>
      ),
      cell: ({ row }) => (
        <div className="text-right text-sm font-medium">
          {formatAmount(row.getValue('rate'))}
          {row.original.quantity_label_text && (
            <>/<span className="text-xs text-muted-foreground">{row.original.quantity_label_text}</span></>
          )}
        </div>
      ),
    },
    {
      id: 'actions',
      header: 'Actions',
      cell: ({ row }) => (
        <div className="text-right">
          <DropdownMenu>
            <DropdownMenuTrigger asChild onClick={(e) => e.stopPropagation()}>
              <Button variant="ghost" size="sm">
                <MoreVertical className="h-5 w-5" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem
                onClick={(e) => {
                  e.stopPropagation();
                  openEditDialog(row.original);
                }}
              >
                <Pencil className="h-4 w-4 mr-2" />
                Edit
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem
                onClick={(e) => {
                  e.stopPropagation();
                  handleDeleteItem(row.original.id);
                }}
                className="text-destructive"
              >
                <Trash2 className="h-4 w-4 mr-2" />
                Delete
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      ),
    },
  ];

  return (
    <>
      <ListPage>
          <ListPageHeader>
            <ListPageTitle>Items</ListPageTitle>
            <ListPageActions>
              {!isViewOnly && (
                <>
                  <Button
                    variant="secondary"
                    className="rounded-full"
                    onClick={() => setImportDialogOpen(true)}
                  >
                    <Upload className="h-4 w-4" />
                    Upload Items
                  </Button>
                  <Button onClick={openAddDialog} className="rounded-full">
                    <Plus className="h-4 w-4" />
                    Add Item
                  </Button>
                </>
              )}
            </ListPageActions>
          </ListPageHeader>

          <ListPageCard>
            <ListPageFilters>
              <Input
                placeholder="Search items..."
                value={searchQuery}
                onChange={(e) => handleSearchChange(e.target.value)}
                className="max-w-sm bg-white"
              />
            </ListPageFilters>

            <ListPageContent>
              <DataTable
                columns={columns}
                data={filteredItems}
                loading={isLoading}
                onRowClick={(item) => openEditDialog(item)}
                noBorder
                emptyIcon={<Package className="h-6 w-6 text-muted-foreground" />}
                emptyTitle={searchQuery ? "No items found" : "No items yet"}
                emptyDescription={searchQuery ? "Try adjusting your search." : "Add your first item to get started."}
                // Server-side pagination
                manualPagination
                manualSorting
                pageSize={PAGE_SIZE}
                pageIndex={pageIndex}
                totalCount={totalCount}
                onPaginationChange={(newPageIndex) => setPageIndex(newPageIndex)}
                sorting={sorting}
                onSortingChange={(newSorting) => {
                  setSorting(newSorting);
                  setPageIndex(0);
                }}
              />
            </ListPageContent>
          </ListPageCard>
        </ListPage>

      {currentBusiness && (
        <ItemDialog
          open={dialogOpen}
          onClose={handleDialogClose}
          businessId={currentBusiness.id}
          item={editingItem}
          onSaved={invalidateItems}
        />
      )}

      {currentBusiness && (
        <ItemImportDialog
          open={importDialogOpen}
          onClose={() => setImportDialogOpen(false)}
          businessId={currentBusiness.id}
          onImported={invalidateItems}
        />
      )}
    </>
  );
}

export function Items() {
  return <ItemsContent />;
}
